<template>
  <div class="rd-page bg-light-page">
    <div class="rd-scroll" ref="scrollEl">

      <!-- ── HERO ──────────────────────────────────── -->
      <div class="rd-hero" :style="heroStyle">
        <div class="rd-hero-bar d-flex align-items-center justify-content-between">
          <button @click="$router.back()" class="rd-glass-btn d-flex align-items-center justify-content-center" type="button">
            <ArrowLeft :size="18" :stroke-width="2.5" />
          </button>
          <div class="rd-status-pill d-flex align-items-center">
            <span class="rd-status-dot"></span>
            <span>{{ RESERVATION.status }}</span>
          </div>
        </div>

        <div class="rd-hero-body">
          <p class="rd-hero-overline mb-2">BOOKING · {{ RESERVATION.bookingId }}</p>
          <h1 class="rd-hero-title mb-1">{{ RESERVATION.room }}</h1>
          <p class="rd-hero-sub mb-0">{{ RESERVATION.floor }} · Room {{ RESERVATION.roomNumber }}</p>
        </div>
      </div>

      <!-- ── STAY SUMMARY CARD ──────────────────────── -->
      <div class="px-3 rd-card-wrap">
        <div class="rd-summary-card">
          <!-- Room strip -->
          <div class="rd-room-strip d-flex align-items-center p-3">
            <div class="rd-room-badge d-flex flex-column align-items-center justify-content-center flex-shrink-0 mr-3">
              <span class="rd-badge-label">Room</span>
              <span class="rd-badge-number">{{ RESERVATION.roomNumber }}</span>
            </div>
            <div class="flex-grow-1 overflow-hidden">
              <p class="mb-0 font-bold text-white rd-room-name">{{ RESERVATION.room }}</p>
              <p class="mb-0 text-white rd-room-meta">
                {{ RESERVATION.guests }} guests · {{ RESERVATION.bedType }}
              </p>
            </div>
            <div class="rd-nights-badge flex-shrink-0 ml-2 d-flex flex-column align-items-center">
              <span class="rd-nights-num">{{ RESERVATION.nights }}</span>
              <span class="rd-nights-label">nights</span>
            </div>
          </div>

          <!-- Date row -->
          <div class="d-flex align-items-center px-3 py-3">
            <div class="flex-grow-1">
              <p class="rd-meta-label mb-1">CHECK-IN</p>
              <p class="mb-0 font-bold rd-date-text">{{ RESERVATION.checkIn }}</p>
            </div>
            <div class="rd-day-pill d-flex flex-column align-items-center justify-content-center flex-shrink-0 mx-2">
              <span class="rd-day-num">{{ RESERVATION.daysIn }}</span>
              <span class="rd-day-of">of {{ RESERVATION.nights }}</span>
            </div>
            <div class="flex-grow-1 text-right">
              <p class="rd-meta-label mb-1">CHECK-OUT</p>
              <p class="mb-0 font-bold rd-date-text">{{ RESERVATION.checkOut }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- ── QUICK ACTIONS ──────────────────────────── -->
      <div class="px-3 rd-quick-wrap">
        <div class="rd-quick-grid">
          <button
            v-for="action in QUICK_ACTIONS"
            :key="action.id"
            @click="onActionClick(action)"
            class="rd-quick-tile"
            type="button"
          >
            <div class="rd-quick-icon" :style="{ color: action.color }">
              <component :is="action.icon" :size="20" :stroke-width="2" />
            </div>
            <p class="rd-quick-label mb-0">{{ action.label }}</p>
            <p class="rd-quick-hint mb-0">{{ action.hint }}</p>
          </button>
        </div>
      </div>

      <!-- ── STAY DETAILS ────────────────────────────── -->
      <div class="px-3 pb-2">
        <div class="rd-section-header mb-3">
          <p class="rd-eyebrow mb-1">Room</p>
          <h2 class="rd-section-title mb-0">Stay details</h2>
        </div>

        <div class="bg-white rd-card overflow-hidden mb-4">
          <div
            v-for="(detail, i) in STAY_DETAILS"
            :key="detail.label"
            :class="['d-flex align-items-center px-3 py-3', { 'border-top': i > 0 }]"
          >
            <div class="rd-icon-box mr-3 flex-shrink-0">
              <component :is="detail.icon" :size="17" :stroke-width="2" />
            </div>
            <div class="flex-grow-1 overflow-hidden">
              <p class="rd-meta-label mb-0">{{ detail.label }}</p>
              <p class="mb-0 font-bold" style="font-size:14px;line-height:1.3">{{ detail.value }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- ── INCLUDED PERKS ─────────────────────────── -->
      <div class="px-3 pb-2">
        <div class="rd-section-header mb-3">
          <p class="rd-eyebrow mb-1">Package</p>
          <h2 class="rd-section-title mb-0">What's included</h2>
        </div>

        <div class="bg-white rd-card p-3 mb-4">
          <div
            v-for="perk in PERKS"
            :key="perk"
            class="d-flex align-items-center mb-2"
            style="gap:10px"
          >
            <div class="rd-perk-tick flex-shrink-0 d-flex align-items-center justify-content-center">
              <Check :size="11" :stroke-width="3.5" />
            </div>
            <p class="mb-0" style="font-size:13.5px;line-height:1.4">{{ perk }}</p>
          </div>
        </div>
      </div>

      <!-- ── RATE BREAKDOWN ─────────────────────────── -->
      <div class="px-3 pb-2">
        <div class="rd-section-header mb-3">
          <p class="rd-eyebrow mb-1">Charges</p>
          <h2 class="rd-section-title mb-0">Rate breakdown</h2>
        </div>

        <div class="bg-white rd-card p-3 mb-4">
          <div class="d-flex justify-content-between py-2">
            <span class="color-grey" style="font-size:13px">Room rate (nightly)</span>
            <span class="font-bold" style="font-size:13px">{{ formatCurrency(RATE.nightly) }}</span>
          </div>
          <div class="d-flex justify-content-between py-2">
            <span class="color-grey" style="font-size:13px">× {{ RATE.nights }} nights</span>
            <span class="font-bold" style="font-size:13px">{{ formatCurrency(subtotal) }}</span>
          </div>
          <div class="d-flex justify-content-between py-2">
            <span class="color-grey" style="font-size:13px">Tax ({{ RATE.taxPct }}%)</span>
            <span class="font-bold" style="font-size:13px">{{ formatCurrency(taxAmount) }}</span>
          </div>
          <div class="d-flex justify-content-between py-2 mb-1">
            <span class="color-grey" style="font-size:13px">Service charge ({{ RATE.servicePct }}%)</span>
            <span class="font-bold" style="font-size:13px">{{ formatCurrency(serviceAmount) }}</span>
          </div>
          <hr class="my-2">
          <div class="d-flex justify-content-between py-2">
            <strong style="font-size:14px">Total stay</strong>
            <strong style="font-size:16px;color:var(--merchant-primary-color,#2B5F7A)">{{ formatCurrency(grandTotal) }}</strong>
          </div>
          <div class="d-flex justify-content-between py-1">
            <span class="color-grey" style="font-size:12.5px">Deposit paid</span>
            <span class="text-green font-bold" style="font-size:12.5px">- {{ formatCurrency(RATE.paid) }}</span>
          </div>
          <div class="rd-balance-row d-flex justify-content-between align-items-center p-3 mt-2">
            <span class="font-bold" style="font-size:13.5px">Balance due at check-out</span>
            <span class="font-bold" style="font-size:17px;color:#C04040">{{ formatCurrency(balanceDue) }}</span>
          </div>
        </div>
      </div>

      <!-- ── MANAGE BOOKING ─────────────────────────── -->
      <div class="px-3 pb-4">
        <div class="rd-section-header mb-3">
          <p class="rd-eyebrow mb-1">Manage</p>
          <h2 class="rd-section-title mb-0">Your booking</h2>
        </div>

        <button
          @click="showToast('Modification request sent — concierge will follow up shortly')"
          class="rd-manage-btn w-100 d-flex align-items-center px-3 py-3 mb-2 bg-white rd-card"
          type="button"
        >
          <div class="rd-icon-box mr-3 flex-shrink-0">
            <Calendar :size="17" :stroke-width="2" />
          </div>
          <div class="flex-grow-1 text-left">
            <p class="mb-0 font-bold" style="font-size:14.5px">Request modification</p>
            <p class="mb-0 color-grey" style="font-size:12px">Date change · room upgrade</p>
          </div>
          <i class="simple-icon-arrow-right color-grey flex-shrink-0" style="font-size:11px"></i>
        </button>

        <button
          @click="showToast('Late check-out requested — subject to availability')"
          class="rd-manage-btn w-100 d-flex align-items-center px-3 py-3 mb-2 bg-white rd-card"
          type="button"
        >
          <div class="rd-icon-box mr-3 flex-shrink-0">
            <Clock :size="17" :stroke-width="2" />
          </div>
          <div class="flex-grow-1 text-left">
            <p class="mb-0 font-bold" style="font-size:14.5px">Request late check-out</p>
            <p class="mb-0 color-grey" style="font-size:12px">Subject to availability</p>
          </div>
          <i class="simple-icon-arrow-right color-grey flex-shrink-0" style="font-size:11px"></i>
        </button>

        <button
          @click="showToast('Connecting you to the front desk…')"
          class="rd-dark-btn w-100 d-flex align-items-center px-3 py-3 rd-card font-bold"
          style="font-size:15px"
          type="button"
        >
          <div class="rd-dark-icon mr-3 flex-shrink-0 d-flex align-items-center justify-content-center">
            <Phone :size="18" :stroke-width="2" />
          </div>
          <span class="flex-grow-1 text-left">Call front desk</span>
          <i class="simple-icon-arrow-right text-white flex-shrink-0" style="font-size:11px"></i>
        </button>
      </div>

      <div style="height:40px"></div>
    </div><!-- /rd-scroll -->

    <!-- Toast -->
    <transition name="rd-fade">
      <div v-if="toast" class="rd-toast">{{ toast }}</div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { formatCurrency } from '@/utils/index.js'
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  MapPin,
  Phone,
  CreditCard,
  Eye,
  Check,
  Maximize2,
  Coffee,
} from 'lucide-vue'

const RESERVATION = {
  guestName:  'Ragil Samsi',
  greeting:   'Ragil',
  bookingId:  'UMA-2026-04812',
  room:       'Garden Pool Villa',
  roomNumber: '214',
  floor:      'Ground floor · West wing',
  guests:     2,
  nights:     4,
  bedType:    'King bed',
  view:       'Garden & pool view',
  size:       '75 m²',
  maxOccupancy: 3,
  checkIn:    'Thu, 7 May 2026',
  checkOut:   'Mon, 11 May 2026',
  status:     'Checked-in',
  daysIn:     1,
}

const RATE = {
  nightly:    1850000,
  nights:     4,
  taxPct:     15,
  servicePct: 10,
  paid:       2000000,
}

const QUICK_ACTIONS = [
  { id: 'bill',      label: 'View Bill',   hint: 'Folio charges',    icon: CreditCard, color: '#2B6FB8' },
  { id: 'modify',    label: 'Modify',      hint: 'Date · room',      icon: Calendar,   color: '#3A8A48' },
  { id: 'late-co',   label: 'Late C/O',    hint: 'Request ext.',     icon: Clock,      color: '#C07B20' },
  { id: 'add-guest', label: 'Add Guest',   hint: 'Extra visitor',    icon: Users,      color: '#7B52B8' },
  { id: 'map',       label: 'Find Room',   hint: 'Hotel map',        icon: MapPin,     color: '#2A8FA8' },
  { id: 'breakfast', label: 'Breakfast',   hint: '07:00 – 10:30',    icon: Coffee,     color: '#C04A30' },
]

const STAY_DETAILS = [
  { icon: Eye,       label: 'Bed type',      value: 'King bed' },
  { icon: Eye,       label: 'View',          value: 'Garden & pool view' },
  { icon: Maximize2, label: 'Room size',     value: '75 m²' },
  { icon: Users,     label: 'Max occupancy', value: '3 adults' },
  { icon: MapPin,    label: 'Location',      value: 'Ground floor · West wing' },
  { icon: Clock,     label: 'Check-out',     value: '12:00 noon · Standard' },
]

const PERKS = [
  'Daily breakfast for 2 guests (07:00 – 10:30)',
  'Welcome drink on arrival',
  'Complimentary high-speed Wi-Fi',
  'Access to infinity pool & fitness centre',
  'Airport shuttle (on request)',
]

export default {
  name: 'ConciergeInquiry',

  components: {
    ArrowLeft, Calendar, Clock, Users, MapPin,
    Phone, CreditCard, Eye, Check, Maximize2, Coffee,
  },

  metaInfo() {
    return {
      title: 'Reservation Detail',
      titleTemplate: this.getMetaTitleTemplate(),
    }
  },

  created() {
    this._toastTimer = null
  },

  data() {
    return {
      RESERVATION,
      RATE,
      QUICK_ACTIONS,
      STAY_DETAILS,
      PERKS,
      toast: null,
    }
  },

  computed: {
    heroStyle() {
      const img = this.getMerchant().merchant_images[0].image_url || ''
      return {
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 45%, rgba(0,0,0,0.75) 100%), url('${img}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    },

    subtotal() {
      return this.RATE.nightly * this.RATE.nights
    },

    taxAmount() {
      return Math.round(this.subtotal * this.RATE.taxPct / 100)
    },

    serviceAmount() {
      return Math.round(this.subtotal * this.RATE.servicePct / 100)
    },

    grandTotal() {
      return this.subtotal + this.taxAmount + this.serviceAmount
    },

    balanceDue() {
      return this.grandTotal - this.RATE.paid
    },
  },

  beforeDestroy() {
    clearTimeout(this._toastTimer)
  },

  methods: {
    ...mapGetters(['getMerchant', 'getMetaTitleTemplate']),

    formatCurrency,

    onActionClick(action) {
      const msgs = {
        bill:      'Opening folio…',
        modify:    'Modification request sent — concierge will follow up shortly',
        'late-co': 'Late check-out requested — subject to availability',
        'add-guest': 'Please call front desk to register an additional guest',
        map:       'Hotel map coming soon',
        breakfast: 'Breakfast is served at Uma Restaurant, 07:00 – 10:30',
      }
      this.showToast(msgs[action.id] || action.label)
    },

    showToast(msg) {
      this.toast = msg
      clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toast = null }, 2200)
    },
  },
}
</script>

<style scoped>
/* ── Page ────────────────────────────────────── */
.rd-page {
  min-height: 100vh;
  position: relative;
}

.rd-scroll {
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

/* ── Hero ────────────────────────────────────── */
.rd-hero {
  padding: 48px 20px 72px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rd-hero-bar {
  position: relative;
  z-index: 2;
}

.rd-glass-btn {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s;
}

.rd-glass-btn:active { background: rgba(0, 0, 0, 0.5); }

.rd-status-pill {
  background: rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  padding: 6px 12px;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
}

.rd-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #22C55E;
  flex-shrink: 0;
  box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  animation: rd-pulse 2s infinite;
}

@keyframes rd-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
  70%  { box-shadow: 0 0 0 5px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}

.rd-hero-body { position: relative; z-index: 2; }

.rd-hero-overline {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.rd-hero-title {
  font-size: 26px;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.6px;
  line-height: 1.1;
  text-shadow: 0 3px 16px rgba(0, 0, 0, 0.4);
}

.rd-hero-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
}

/* ── Stay summary card ────────────────────────── */
.rd-card-wrap {
  margin-top: -48px;
  position: relative;
  z-index: 2;
  padding-bottom: 16px;
}

.rd-summary-card {
  border-radius: 18px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.10);
}

.rd-room-strip {
  background: linear-gradient(135deg, var(--merchant-primary-color, #2B5F7A) 0%, #1a3a4b 100%);
  position: relative;
}

.rd-room-strip::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.1), transparent 70%);
  pointer-events: none;
}

.rd-room-badge {
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 12px;
  flex-shrink: 0;
}

.rd-badge-label {
  font-size: 9px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.rd-badge-number {
  font-size: 24px;
  color: #fff;
  line-height: 1;
  font-family: Lato-Bold, sans-serif;
}

.rd-room-name {
  font-size: 15px;
  line-height: 1.25;
  position: relative;
  z-index: 1;
}

.rd-room-meta {
  font-size: 11.5px;
  opacity: 0.8;
  position: relative;
  z-index: 1;
}

.rd-nights-badge {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 6px 10px;
  position: relative;
  z-index: 1;
}

.rd-nights-num {
  font-size: 20px;
  font-weight: 900;
  color: #fff;
  line-height: 1;
}

.rd-nights-label {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.rd-meta-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6B7886;
}

.rd-date-text { font-size: 13.5px; }

.rd-day-pill {
  background: #EDFBF3;
  border: 1.5px solid #86EFAC;
  border-radius: 12px;
  padding: 6px 12px;
}

.rd-day-num {
  font-size: 18px;
  font-weight: 900;
  color: #16A34A;
  line-height: 1;
}

.rd-day-of {
  font-size: 9px;
  font-weight: 700;
  color: #16A34A;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ── Quick actions ───────────────────────────── */
.rd-quick-wrap {
  padding-bottom: 24px;
}

.rd-quick-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
}

.rd-quick-tile {
  background: #fff;
  border: none;
  border-radius: 16px;
  padding: 13px 4px 11px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  box-shadow: 0 1px 2px rgba(40, 45, 55, 0.05), 0 3px 8px rgba(40, 45, 55, 0.06);
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.12s ease;
  -webkit-tap-highlight-color: transparent;
}

.rd-quick-tile:active {
  transform: scale(0.94);
  box-shadow: none;
}

.rd-quick-icon {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: #F3F4F6;
  border: 1px solid rgba(150, 150, 160, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rd-quick-label {
  font-size: 10px;
  font-weight: 800;
  color: #1C2030;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-align: center;
  line-height: 1.2;
}

.rd-quick-hint {
  font-size: 9px;
  font-weight: 500;
  color: #888EA0;
  text-align: center;
  line-height: 1.25;
}

/* ── Section headers ─────────────────────────── */
.rd-section-header { padding-top: 4px; }

.rd-eyebrow {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #6B7886;
}

.rd-section-title {
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.2px;
  line-height: 1.15;
  color: #1C2B36;
}

/* ── Cards ───────────────────────────────────── */
.rd-card {
  border-radius: 16px;
  border: 1px solid #E0E8EF;
  box-shadow: 0 1px 2px rgba(15, 30, 50, 0.04), 0 4px 14px rgba(15, 30, 50, 0.06);
}

/* ── Icon box ────────────────────────────────── */
.rd-icon-box {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #E6EFF4;
  color: var(--merchant-primary-color, #2B5F7A);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Perks ───────────────────────────────────── */
.rd-perk-tick {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  flex-shrink: 0;
}

/* ── Rate balance row ────────────────────────── */
.rd-balance-row {
  background: #FFF5F5;
  border-radius: 12px;
  border: 1.5px solid #FCA5A5;
}

/* ── Manage buttons ──────────────────────────── */
.rd-manage-btn {
  background: #fff;
  border: 1px solid #E0E8EF;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 30, 50, 0.04), 0 4px 14px rgba(15, 30, 50, 0.06);
  cursor: pointer;
  font-family: inherit;
  color: #1C2B36;
  transition: background 0.12s;
  -webkit-tap-highlight-color: transparent;
}

.rd-manage-btn:active { background: #F3F6F9; }

.rd-dark-btn {
  background: #1C2B36;
  color: #fff;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  transition: filter 0.12s;
}

.rd-dark-btn:active { filter: brightness(0.88); }

.rd-dark-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
}

/* ── Toast ───────────────────────────────────── */
.rd-toast {
  position: fixed;
  bottom: 36px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(28, 43, 54, 0.93);
  backdrop-filter: blur(8px);
  color: #fff;
  padding: 11px 20px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  z-index: 1060;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  white-space: normal;
  pointer-events: none;
  max-width: calc(100vw - 40px);
  text-align: center;
  line-height: 1.35;
}

.rd-fade-enter-active, .rd-fade-leave-active { transition: opacity 0.25s; }
.rd-fade-enter, .rd-fade-leave-to { opacity: 0; }
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/ConciergeInquiry.vue